import { useEffect, useRef, useState } from "react";
import { PostInfo } from "../../components/post/Post";
import { PostApiRoutes } from "../api-routes/post/post.api-routes";
import http from "../utils/http";

interface MediaStatusResponse {
	mediaStatus: "processing" | "ready" | "failed";
	postProgress?: number;
}

/**
 * Custom hook to poll for media processing status.
 * Uses the lightweight GET /post/:id/media-status endpoint while processing,
 * then fetches the full post once when ready.
 */
export const useMediaProcessing = (
	postInfo: PostInfo,
	onUpdate?: (updatedPost: PostInfo) => void,
) => {
	const [currentPost, setCurrentPost] = useState<PostInfo>(postInfo);
	const intervalRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (currentPost.mediaStatus !== "processing" || !currentPost.id) return;

		intervalRef.current = setInterval(async () => {
			try {
				const statusRes = await http.get<MediaStatusResponse>(
					PostApiRoutes.GET_MEDIA_STATUS(currentPost.id),
					{ skipGlobalLoader: true } as any,
				);

				if (
					statusRes.mediaStatus === "ready" ||
					statusRes.mediaStatus === "failed"
				) {
					if (intervalRef.current) {
						clearInterval(intervalRef.current);
						intervalRef.current = null;
					}

					if (statusRes.mediaStatus === "ready") {
						// Fetch full post once to get complete media URLs
						const fullPost = await http.get<PostInfo>(
							PostApiRoutes.GET_POST(currentPost.id),
							{ skipGlobalLoader: true } as any,
						);
						setCurrentPost(fullPost);
						if (onUpdate) onUpdate(fullPost);
					} else {
						const failed = {
							...currentPost,
							mediaStatus: statusRes.mediaStatus as "failed",
						};
						setCurrentPost(failed);
						if (onUpdate) onUpdate(failed);
					}
				} else {
					// Still processing — update progress only
					setCurrentPost((prev) => ({
						...prev,
						mediaStatus: statusRes.mediaStatus,
						postProgress: statusRes.postProgress,
					}));
				}
			} catch (error) {
				console.error("Error polling media status:", error);
				if (intervalRef.current) {
					clearInterval(intervalRef.current);
					intervalRef.current = null;
				}
			}
		}, 2000);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [currentPost.id, currentPost.mediaStatus, onUpdate]);

	// Sync with prop changes
	useEffect(() => {
		setCurrentPost(postInfo);
	}, [postInfo]);

	return {
		currentPost,
		isProcessing: currentPost.mediaStatus === "processing",
		hasFailed: currentPost.mediaStatus === "failed",
		isReady: currentPost.mediaStatus === "ready",
		progress: currentPost.postProgress || 0,
	};
};
